import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { decryptJson } from "@/lib/vaultCrypto";
import { maybeNotify } from "@/lib/vaultReminders";

const VaultContext = createContext(null);
const AUTO_LOCK_MS = 15 * 60 * 1000;

export function VaultProvider({ children }) {
  const { user } = useAuth();
  const [vaultKey, setVaultKey] = useState(null);
  const [entries, setEntries] = useState([]);
  const timer = useRef(null);

  const lock = useCallback(() => {
    setVaultKey(null);
    setEntries([]);
  }, []);

  useEffect(() => { if (!user) lock(); }, [user, lock]);
  useEffect(() => { if (user) maybeNotify(); }, [user]);

  const touch = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    if (vaultKey) timer.current = setTimeout(lock, AUTO_LOCK_MS);
  }, [vaultKey, lock]);

  useEffect(() => {
    touch();
    if (!vaultKey) return undefined;
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, touch));
    return () => events.forEach((e) => window.removeEventListener(e, touch));
  }, [vaultKey, touch]);

  const decryptEntry = useCallback(async (key, e) => {
    try {
      const data = await decryptJson(key, e.ciphertext, e.iv);
      const attachments = await Promise.all(
        (e.attachments || []).map(async (a) => {
          try {
            return { ...a, meta: await decryptJson(key, a.meta_ciphertext, a.meta_iv) };
          } catch {
            return { ...a, meta: { name: "Unreadable attachment", type: "" } };
          }
        })
      );
      return { ...e, data, attachments };
    } catch {
      return { ...e, data: null, attachments: [], corrupt: true };
    }
  }, []);

  const loadEntries = useCallback(async (key = vaultKey) => {
    if (!key) return [];
    const { data } = await api.get("/vault/entries");
    const list = await Promise.all(data.map((e) => decryptEntry(key, e)));
    setEntries(list);
    return list;
  }, [vaultKey, decryptEntry]);

  const unlock = useCallback(async (key) => {
    setVaultKey(key);
    await loadEntries(key);
  }, [loadEntries]);

  return (
    <VaultContext.Provider value={{ vaultKey, unlocked: !!vaultKey, entries, unlock, lock, loadEntries, setEntries }}>
      {children}
    </VaultContext.Provider>
  );
}

export const useVault = () => useContext(VaultContext);
